import { Injectable } from '@angular/core';
import { Department } from '../../domain/models/department.model';
import {
  CATALOG_PLAN_ACCENT_TOKENS,
  CatalogPlan,
  CatalogPlanAccentToken,
  CatalogPlanEntitlement,
  CatalogPlanEntitlementKind,
} from '@shared/models/plan-catalog.model';
import { OnboardingPlanSelection } from '../../domain/models/onboarding-plan.model';
import { OnboardingStatus, OnboardingTrialCodeRedemption } from '../../domain/models/onboarding-status.model';
import { OrganizationOverview } from '../../domain/models/organization-overview.model';
import { PersonalData } from '../../domain/models/personal-data.model';
import { StaffMember } from '../../domain/models/staff-member.model';
import { OnboardingTeamDraft, OnboardingTeamDraftSelection } from '../../domain/models/onboarding-team-draft.model';
import {
  CheckoutStatusResult,
  PaymentConfig,
  SUBSCRIPTION_CHECKOUT_COMPLETED_LOCALLY,
  SUBSCRIPTION_CHECKOUT_SESSION_STATUSES,
  SubscriptionCheckout,
  SubscriptionCheckoutSessionStatus,
} from '../../domain/models/subscription-checkout.model';
import {
  CheckoutStatusDto,
  OnboardingDepartmentDto,
  OnboardingOrganizationDto,
  OnboardingPersonalDataDto,
  OnboardingPlanDto,
  OnboardingPlanEntitlementDto,
  OnboardingProfessionalDto,
  OnboardingStatusDto,
  OnboardingTeamDraftDto,
  OnboardingTrialCodeRedemptionDto,
  PaymentConfigDto,
  RedeemOnboardingTrialCodeRequestDto,
  SelectOnboardingPlanRequestDto,
  SaveOnboardingTeamDraftRequestDto,
  StartSubscriptionCheckoutRequestDto,
  SubscriptionCheckoutDto,
} from '../dtos/onboarding.dto';

const DEFAULT_ACCENT_TOKEN: CatalogPlanAccentToken = 'indigo';

// A session whose status this build does not know must not be treated as payable: it is reported as
// expired so step 5 offers a retry instead of mounting a form against an unusable session.
const DEFAULT_SESSION_STATUS: SubscriptionCheckoutSessionStatus = 'expired';

@Injectable()
export class OnboardingMapper {
  public toStatus(dto: OnboardingStatusDto): OnboardingStatus {
    return {
      step1: dto.Step1,
      step2: dto.Step2,
      step3: dto.Step3,
      step4: dto.Step4,
      step5: dto.Step5,
      isComplete: dto.IsComplete,
    };
  }

  public toTrialCodeRedemption(dto: OnboardingTrialCodeRedemptionDto): OnboardingTrialCodeRedemption {
    return {
      status: this.toStatus(dto.Status),
      trialDays: dto.TrialDays,
    };
  }

  public toPersonalData(dto: OnboardingPersonalDataDto): PersonalData {
    return {
      externalId: dto.ExternalId,
      firstName: dto.FirstName,
      lastName: dto.LastName,
      email: dto.Email,
      phone: dto.Phone,
      province: dto.Province,
      pictureUrl: dto.PictureUrl,
    };
  }

  public toOrganizationOverview(dto: OnboardingOrganizationDto): OrganizationOverview {
    return {
      externalId: dto.ExternalId,
      name: dto.Name,
      departments: dto.Departments.map((department) => this.toDepartment(department)),
    };
  }

  public toDepartment(dto: OnboardingDepartmentDto): Department {
    return { externalId: dto.ExternalId, name: dto.Name, id: dto.Id ?? null };
  }

  public toStaffMember(dto: OnboardingProfessionalDto): StaffMember {
    return {
      externalId: dto.ExternalId,
      firstName: dto.FirstName,
      lastName: dto.LastName,
      displayName: dto.DisplayName,
      email: dto.Email,
      role: dto.Role,
    };
  }

  public toTeamDraft(dto: OnboardingTeamDraftDto): OnboardingTeamDraft {
    return {
      revision: dto.Revision,
      updatedAtUtc: dto.UpdatedAtUtc ?? null,
      members: dto.Members.map((member) => ({
        professionalExternalId: member.ProfessionalExternalId,
        email: member.Email,
        firstName: member.FirstName,
        lastName: member.LastName,
        targetRole: member.TargetRole,
      })),
    };
  }

  public toSaveTeamDraftRequest(
    revision: number,
    members: readonly OnboardingTeamDraftSelection[],
  ): SaveOnboardingTeamDraftRequestDto {
    return {
      Revision: revision,
      Members: members.map((member) => ({
        ProfessionalExternalId: member.professionalExternalId,
        TargetRole: member.targetRole,
      })),
    };
  }

  public toPlans(dtos: readonly OnboardingPlanDto[]): readonly CatalogPlan[] {
    return (dtos ?? []).map((dto) => this.toPlan(dto));
  }

  public toPlan(dto: OnboardingPlanDto): CatalogPlan {
    return {
      id: dto.Id,
      code: dto.Code,
      name: dto.Name,
      shortDescription: dto.ShortDescription ?? null,
      accentToken: this.toAccentToken(dto.AccentToken),
      monthlyPrice: dto.MonthlyPrice ?? null,
      annualPrice: dto.AnnualPrice ?? null,
      setupFee: dto.SetupFee ?? null,
      trialDays: dto.TrialDays ?? null,
      currency: dto.Currency,
      isMostContracted: dto.IsMostContracted,
      displayOrder: dto.DisplayOrder,
      entitlements: (dto.Entitlements ?? []).map((entitlement) => this.toEntitlement(entitlement)),
    };
  }

  public toSelectPlanRequest(selection: OnboardingPlanSelection): SelectOnboardingPlanRequestDto {
    return {
      PlanId: selection.planId,
      BillingPeriod: selection.billingPeriod,
      PreferredPaymentMethod: selection.preferredPaymentMethod,
    };
  }

  public toRedeemTrialCodeRequest(code: string): RedeemOnboardingTrialCodeRequestDto {
    return { Code: code };
  }

  public toStartCheckoutRequest(
    selection: OnboardingPlanSelection,
  ): StartSubscriptionCheckoutRequestDto {
    return {
      PlanId: selection.planId,
      BillingPeriod: selection.billingPeriod,
      PreferredPaymentMethod: selection.preferredPaymentMethod,
    };
  }

  public toSubscriptionCheckout(dto: SubscriptionCheckoutDto): SubscriptionCheckout {
    const status = (dto.Status ?? '').trim().toLowerCase();
    return {
      sessionId: dto.SessionId ?? null,
      clientSecret: dto.ClientSecret ?? null,
      publishableKey: dto.PublishableKey ?? '',
      status:
        status === SUBSCRIPTION_CHECKOUT_COMPLETED_LOCALLY
          ? SUBSCRIPTION_CHECKOUT_COMPLETED_LOCALLY
          : this.toSessionStatus(status),
    };
  }

  public toCheckoutStatusResult(dto: CheckoutStatusDto): CheckoutStatusResult {
    return {
      status: this.toSessionStatus(dto.Status),
      onboardingStatus: this.toStatus(dto.OnboardingStatus),
    };
  }

  public toPaymentConfig(dto: PaymentConfigDto): PaymentConfig {
    return {
      provider: dto.Provider ?? '',
      publishableKey: dto.PublishableKey ?? '',
      enabled: dto.Enabled,
    };
  }

  private toSessionStatus(value: string): SubscriptionCheckoutSessionStatus {
    const normalized = (value ?? '').trim().toLowerCase();
    return (
      SUBSCRIPTION_CHECKOUT_SESSION_STATUSES.find((status) => status === normalized) ??
      DEFAULT_SESSION_STATUS
    );
  }

  private toEntitlement(dto: OnboardingPlanEntitlementDto): CatalogPlanEntitlement {
    return {
      id: dto.Id,
      kind: this.toEntitlementKind(dto.Kind),
      entitlementKey: dto.EntitlementKey,
      limitValue: dto.LimitValue ?? null,
      isUnlimited: dto.IsUnlimited,
      isIncluded: dto.IsIncluded,
      displayValue: dto.DisplayValue ?? null,
      monthlySurcharge: dto.MonthlySurcharge ?? null,
      displayOrder: dto.DisplayOrder,
    };
  }

  private toAccentToken(value: string): CatalogPlanAccentToken {
    const normalized = (value ?? '').trim().toLowerCase();
    return CATALOG_PLAN_ACCENT_TOKENS.find((token) => token === normalized) ?? DEFAULT_ACCENT_TOKEN;
  }

  private toEntitlementKind(value: string): CatalogPlanEntitlementKind {
    return (value ?? '').trim().toLowerCase() === 'module' ? 'module' : 'limit';
  }
}
